import {
  ClampToEdgeWrapping,
  DataTexture,
  LinearFilter,
  LinearMipmapLinearFilter,
  NoColorSpace,
  RepeatWrapping,
  RGBAFormat,
  UnsignedByteType,
} from 'three'

/**
 * Texturas procedurais: geradas aqui, uma vez, sem baixar nada.
 *
 * A aula às vezes acontece sem rede, então nenhuma textura vem de arquivo ou
 * de endereço externo. Cada gerador é uma função pura que devolve pixels
 * (RGBA, 8 bits), de modo que o padrão se testa em Node, sem WebGL; a criação
 * da `DataTexture` é a única parte que toca o three, e fica em cache.
 */
export interface ImagemProcedural {
  readonly largura: number
  readonly altura: number
  /** RGBA, linha a linha, de baixo para cima (a ordem que o WebGL lê). */
  readonly dados: Uint8Array
}

const limitar = (x: number, minimo: number, maximo: number) => Math.min(maximo, Math.max(minimo, x))

/** 0 abaixo de `a`, 1 acima de `b`, com transição suave entre os dois. */
function suave(a: number, b: number, x: number): number {
  const t = limitar((x - a) / (b - a), 0, 1)
  return t * t * (3 - 2 * t)
}

const parteFracionaria = (x: number) => x - Math.floor(x)

/** Altura (0 a 1) da banda de rodagem num ponto (u em volta do pneu, v de ombro a ombro). */
export function alturaDaBandaDeRodagem(u: number, v: number, blocos: number): number {
  // fora da banda (os flancos), o relevo é liso e no mesmo nível do topo dos
  // blocos: sem degrau, o mapa não desenha um friso na borda da banda
  const NIVEL_DO_TOPO = 0.9
  const dentro = suave(0.27, 0.33, v) * (1 - suave(0.67, 0.73, v))
  if (dentro === 0) return NIVEL_DO_TOPO

  // sulcos inclinados, em espinha: a inclinação inverte no meio da banda
  const lado = v < 0.5 ? -1 : 1
  const fase = parteFracionaria(u * blocos + lado * (v - 0.5) * 1.6)
  const sulco = 1 - suave(0.1, 0.2, fase) * (1 - suave(0.26, 0.36, fase))

  // nervura central contínua, sem sulco
  const nervura = 1 - suave(0.04, 0.07, Math.abs(v - 0.5))

  const profundidade = 0.8
  const relevo = NIVEL_DO_TOPO - sulco * profundidade * (1 - nervura)
  return NIVEL_DO_TOPO + (relevo - NIVEL_DO_TOPO) * dentro
}

/**
 * Mapa de altura da banda de rodagem de um pneu. A largura cobre `blocos` blocos
 * em volta, e a textura se repete na volta inteira: a primeira e a última coluna
 * se encontram sem emenda.
 */
export function gerarBandaDeRodagem(largura = 128, altura = 64, blocos = 15): ImagemProcedural {
  const dados = new Uint8Array(largura * altura * 4)
  for (let y = 0; y < altura; y++) {
    for (let x = 0; x < largura; x++) {
      const h = alturaDaBandaDeRodagem(x / largura, (y + 0.5) / altura, blocos)
      const valor = Math.round(limitar(h, 0, 1) * 255)
      const i = (y * largura + x) * 4
      dados[i] = valor
      dados[i + 1] = valor
      dados[i + 2] = valor
      dados[i + 3] = 255
    }
  }
  return { largura, altura, dados }
}

let bandaDeRodagem: DataTexture | null = null

/**
 * Textura de relevo da banda de rodagem, repetida três vezes em volta do pneu
 * (45 blocos no total). Uma só para todos os pneus.
 */
export function texturaDaBandaDeRodagem(): DataTexture {
  if (bandaDeRodagem) return bandaDeRodagem
  const imagem = gerarBandaDeRodagem()
  const textura = new DataTexture(imagem.dados, imagem.largura, imagem.altura, RGBAFormat, UnsignedByteType)
  textura.wrapS = RepeatWrapping
  textura.wrapT = ClampToEdgeWrapping
  textura.repeat.set(3, 1)
  textura.generateMipmaps = true
  textura.minFilter = LinearMipmapLinearFilter
  textura.magFilter = LinearFilter
  textura.anisotropy = 4
  // relevo é dado, não cor: não passa por conversão de espaço de cor
  textura.colorSpace = NoColorSpace
  textura.needsUpdate = true
  bandaDeRodagem = textura
  return textura
}
